import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { callApi } from '../../../../api/call';
import { useAuth } from '../../../../context/AuthContext';
import BackToPaymentSettings from '../../components/BackToPaymentSettings';
import { PageSkeleton } from '../../../loading/skeletons';

const DEFAULT_CONFIG = {
  defaultEnvironment: 'PRODUCTION',
  authorizeEnvironment: 'INHERIT',
  deluxeEnvironment: 'INHERIT',
};

const ENV_LABELS = {
  PRODUCTION: 'Production',
  SANDBOX: 'Sandbox',
  INHERIT: 'Use default',
};

const PaymentGatewayEnvironmentSettings = ({ onBackToPayments }) => {
  const { companyData, updateCompanyData, user } = useAuth();
  const isSpecialAdmin = !!user?.isSpecialAdmin;
  const [form, setForm] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loadConfig = async () => {
      if (!isSpecialAdmin) {
        setLoading(false);
        return;
      }
      if (!companyData?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await callApi(
          `/api/companies/${encodeURIComponent(companyData.id)}/payments/environment/get`,
          { companyId: companyData.id }
        );
        setForm({ ...DEFAULT_CONFIG, ...(response.data?.config || {}) });
      } catch (e) {
        console.error('Failed to load payment environment settings:', e);
        setError('Failed to load payment environment settings.');
      } finally {
        setLoading(false);
      }
    };

    void loadConfig();
  }, [companyData?.id, isSpecialAdmin]);

  const handleSave = async () => {
    if (!companyData?.id || !isSpecialAdmin) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await callApi(
        `/api/companies/${encodeURIComponent(companyData.id)}/payments/environment`,
        { companyId: companyData.id, config: form }
      );
      const savedConfig = { ...DEFAULT_CONFIG, ...(response.data?.config || form) };
      setForm(savedConfig);
      if (updateCompanyData) {
        updateCompanyData({
          ...companyData,
          paymentGatewayEnvironment: savedConfig,
        });
      }
      setSuccess('Payment environment settings saved.');
    } catch (e) {
      console.error('Failed to save payment environment settings:', e);
      setError(e?.message || 'Failed to save payment environment settings.');
    } finally {
      setSaving(false);
    }
  };

  const environmentFields = [
    {
      key: 'deluxeEnvironment',
      label: 'Deluxe environment',
      helper: 'Use default, or force Deluxe to Production/Sandbox.',
      allowInherit: true,
    },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <PageSkeleton showHeader={false} variant="form" rows={4} label="Loading payment environment" />
      </Box>
    );
  }

  return (
    <Box>
      {onBackToPayments && <BackToPaymentSettings onBack={onBackToPayments} />}
      {!isSpecialAdmin && (
        <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 720 }}>
          <Alert severity="info">
            Only special admins can manage payment production/sandbox environments.
          </Alert>
        </Paper>
      )}
      {isSpecialAdmin && (
      <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 720 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
          Payment Gateway Environments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Switch between production and sandbox quickly for Deluxe.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel id="default-payment-env-label">Default environment</InputLabel>
            <Select
              labelId="default-payment-env-label"
              value={form.defaultEnvironment}
              label="Default environment"
              onChange={(event) =>
                setForm((prev) => ({ ...prev, defaultEnvironment: event.target.value }))
              }
            >
              <MenuItem value="PRODUCTION">Production</MenuItem>
              <MenuItem value="SANDBOX">Sandbox</MenuItem>
            </Select>
            <FormHelperText>
              Applies to all gateways unless a gateway override is set.
            </FormHelperText>
          </FormControl>

          {environmentFields.map((field) => (
            <FormControl key={field.key} fullWidth>
              <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
              <Select
                labelId={`${field.key}-label`}
                value={form[field.key]}
                label={field.label}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, [field.key]: event.target.value }))
                }
              >
                {field.allowInherit && <MenuItem value="INHERIT">{ENV_LABELS.INHERIT}</MenuItem>}
                <MenuItem value="PRODUCTION">{ENV_LABELS.PRODUCTION}</MenuItem>
                <MenuItem value="SANDBOX">{ENV_LABELS.SANDBOX}</MenuItem>
              </Select>
              <FormHelperText>{field.helper}</FormHelperText>
            </FormControl>
          ))}

          <Box sx={{ pt: 1 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save environments'}
            </Button>
          </Box>
        </Stack>
      </Paper>
      )}
    </Box>
  );
};

export default PaymentGatewayEnvironmentSettings;

